import { Component, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { AlertCircle } from 'lucide-react'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  showDialog?: boolean
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: { componentStack: string } | null
  eventId: string | null
}

/**
 * Error Boundary component with Sentry integration
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs them to Sentry, and displays a fallback UI.
 *
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 *
 * @example Custom fallback
 * ```tsx
 * <ErrorBoundary fallback={<div>Custom error message</div>}>
 *   <Component />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: { componentStack: string }): void {
    // Log error to Sentry
    Sentry.withScope((scope) => {
      scope.setContext('errorInfo', {
        componentStack: errorInfo.componentStack,
      })
      const eventId = Sentry.captureException(error)
      this.setState({ eventId, errorInfo })
    })

    // Also log to console for development
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    })
  }

  handleReportFeedback = (): void => {
    const { eventId } = this.state
    if (eventId) {
      // Open Sentry user feedback dialog
      Sentry.showReportDialog({ eventId })
    }
  }

  render(): ReactNode {
    const { hasError, error, eventId } = this.state
    const { children, fallback, showDialog = true } = this.props

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback
      }

      // Default error UI
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-destructive" />
                <CardTitle>Something went wrong</CardTitle>
              </div>
              <CardDescription>
                We're sorry for the inconvenience. An unexpected error occurred.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {error && (
                <div className="rounded-md bg-muted p-3">
                  <p className="text-sm font-mono text-muted-foreground break-all">
                    {error.toString()}
                  </p>
                </div>
              )}
              <p className="text-sm text-muted-foreground">
                This error has been automatically reported to our team. You can help us by providing
                additional details about what you were doing when this happened.
              </p>
            </CardContent>
            <CardFooter className="flex flex-col gap-2 sm:flex-row">
              <Button onClick={this.handleReset} className="w-full sm:w-auto">
                Try Again
              </Button>
              {showDialog && eventId && (
                <Button
                  onClick={this.handleReportFeedback}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  Report Feedback
                </Button>
              )}
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="w-full sm:w-auto"
              >
                Reload Page
              </Button>
            </CardFooter>
          </Card>
        </div>
      )
    }

    return children
  }
}

/**
 * Sentry's built-in Error Boundary for simpler use cases
 *
 * This is a drop-in replacement that uses Sentry's default error UI
 *
 * @example
 * ```tsx
 * <SentryErrorBoundary fallback={<div>Error occurred</div>}>
 *   <App />
 * </SentryErrorBoundary>
 * ```
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary

export default ErrorBoundary
